import PropTypes from 'prop-types';
import hasDom from 'has-dom';
import React, { memo, useMemo, useCallback, useRef, useState, useEffect } from 'react';
import { useDevice } from '@atc/react-device';
import _get from 'lodash/get';
import { sendClick, sendImpressions } from 'reaxl-analytics';
import { useFeatures } from '@atc/features';
import EmailContext from '../EmailContext';
import EmailSideBarToModal from '../EmailSideBarToModal';
import EmailForm from '../EmailForm';
import EmailStaticContent from '../EmailStaticContent';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const defaultInquiry = 'confirmAvailability';

/**
 *  EmailOwner is a shell component of email title, header, body and footer
 *  which also hosts email CTA and show/hide SideBarToModal
 */

/**
 * EmailOwner component handles the email inquiry flow for vehicle listings, including form submission,
 * inquiry selection, additional inventory, and confirmation modals.
 *
 * @component
 * @param {Object} props
 * @param {React.ReactNode} [props.additionalContent] - Additional content to display in the email form/modal.
 * @param {React.ReactNode} [props.alternateFormContent] - Alternate content for the email form.
 * @param {'primary'|'secondary'|string} [props.buttonStyle='primary'] - Style for the submit button.
 * @param {string} [props.clickType] - Type of click event for lead analytics.
 * @param {Object} [props.ctaProps={}] - Props for the CTA modal/sidebar.
 * @param {Object} [props.dealsDuck] - Duck containg current deal information.
 * @param {string} [props.emailCTAContext] - The emailCTAContext used for determining which modal is opened.
 * @param {React.ReactNode} [props.footerContent] - Footer content for the emailForm.
 * @param {Array} [props.formattedFilterValues=[]] - Filter values formatted for display.
 * @param {Function} [props.handleAdditionalInventoryOpen] - Handler for opening additional inventory modal.
 * @param {Function} [props.handleAdditionalInventoryModalClose] - Handler for closing additional inventory modal and resetting additional inventory data.
 * @param {Function} [props.handleAdditionalInventoryModalOpen] - Handler for setting the interaction duck whenever the additional inventory modal is opened.
 * @param {Function} [props.handleEditSettingsClick] - Handler for opening the edit oneClick setting modal.
 * @param {Function} [props.handleEmailInputInteraction] - Handler for email input interaction.
 * @param {Function} [props.handleOneClickEmailSend] - Handler for sending oneClick leads.
 * @param {Function} [props.handleSaveChanges] - Handler for saving changes in the edit uiContext.
 * @param {Function} [props.handleVHROpenNewTab] - Handler for opening Vehicle History Report in new tab.
 * @param {boolean} [props.isEditEmailModal=false] - Whether the modal is the oneClick edit opt in uiContext.
 * @param {boolean} [props.isIncentivesSheet] - Whether the modal part of the incentives sheet.
 * @param {Object} [props.listing={}] - Listing data for the vehicle.
 * @param {boolean} [props.messageOptIn] - Whether the user has opted in for SMS messaging.
 * @param {Object} [props.ndsProps] -Props for submitting a lead on NDS.
 * @param {boolean} [props.ndsProps.useFallbackLead=false] - Use fallback lead if NDS fails.
 * @param {Object} [props.nextStepsProps={}] - Props that store the next steps after a user submits a lead. Contains additional inventory, next best actions, and similar inventory.
 * @param {boolean} [props.oneClickChecked] - Whether oneclick checkbox is checked.
 * @param {boolean} [props.oneClickEmailSent] - Whether oneclick email was sent.
 * @param {boolean} [props.oneClickAdditionalInventory] - Boolean to display the additional inventory when successfully submitting a oneClick lead.
 * @param {Function} [props.onEmailSubmit] - Function for submitting and sending the lead.
 * @param {Object} [props.owner={}] - Owner object for the listing.
 * @param {Function} [props.phoneClickHandler] - Handler for phone click events.
 * @param {Function} [props.setDealInfo] - Setter to update the user's deal info when sending a lead.
 * @param {Function} [props.setEmailConfirmationModal] - Setter for showing the email confirmation modal.
 * @param {Function} [props.handleIncentivesSheetClose] - Setter for closing the incentives sheet when email is submitted.
 * @param {Function} [props.setMessageOptIn] - Setter for the SMS message opted-in state..
 * @param {Function} [props.setOneClickChecked] - Setter to change boolean that controls whether the oneClick checkbox is checked.
 * @param {Function} [props.setOneClickEmailSent] - Setter for boolean that controls the one click sent state.
 * @param {boolean} [props.showEmailConfirmationModal] - Boolean to show the email confirmation modal.
 * @param {string} [props.sideBarToModalTitle] - Title for the sidebar-to-modal component.
 * @param {string} [props.uiContext='default'] - UI context used for styling and structuring the email form.
 * @param {Object} [props.userDuck] - The duck containing user information state.
 * @param {Object} [props.userProfile={}] - The lead profile containing user's identifying info.
 * @param {boolean} [props.isLoggedIn] - Boolean containing whether the user is logged in.
 * @param {string|number} [props.zip] - The listing's or user's zip code depending on what is available.
 *
 * @returns {React.ReactElement}
 */
const EmailOwner = /*#__PURE__*/memo(_ref => {
  let {
    additionalContent,
    alternateFormContent,
    buttonStyle = 'primary',
    clickType,
    ctaProps = {},
    dealsDuck,
    emailCTAContext,
    footerContent,
    formattedFilterValues = [],
    handleAdditionalInventoryOpen = () => {},
    handleAdditionalInventoryModalClose = () => {},
    handleAdditionalInventoryModalOpen = () => {},
    handleEditSettingsClick,
    handleEmailInputInteraction,
    handleOneClickEmailSend = () => {},
    handleSaveChanges = () => {},
    handleVHROpenNewTab = () => {},
    isEditEmailModal = false,
    isIncentivesSheet,
    listing = {},
    messageOptIn,
    ndsProps: {
      useFallbackLead = false,
      ...ndsProps
    } = {},
    nextStepsProps = {},
    oneClickChecked,
    oneClickEmailSent,
    oneClickAdditionalInventory,
    onEmailSubmit = () => {},
    owner = {},
    phoneClickHandler = () => {},
    setDealInfo = () => {},
    setEmailConfirmationModal,
    handleIncentivesSheetClose = () => {},
    setMessageOptIn,
    setOneClickChecked,
    setOneClickEmailSent = () => {},
    showEmailConfirmationModal,
    sideBarToModalTitle,
    uiContext = 'default',
    userDuck,
    userProfile = {},
    isLoggedIn,
    zip,
    ...rest
  } = _ref;
  const {
    accelerate: [, {
      test_drive: isAccelerateTestDriveEnabled
    }],
    brand: [, {
      channel
    }],
    enable_similar_listings_in_additional_inventory: [isSimilarListingsEnabled],
    my_wallet: [, {
      enable_deals: enableDeals
    }]
  } = useFeatures(['accelerate', 'brand', 'enable_similar_listings_in_additional_inventory', 'my_wallet']);
  const device = useDevice();
  const isXs = _get(device, 'is.xs', true);
  const isSm = _get(device, 'is.sm', true);
  const {
    id: listingId,
    paymentServices,
    marketExtension,
    pricingDetail = {}
  } = listing;
  const isAccelerateScheduleTestDrive = paymentServices?.digitalRetailingType === 'accelerate' && isAccelerateTestDriveEnabled;
  const hideTestDrive = marketExtension?.isVirtualDealer;
  const {
    nextSteps,
    getAdditionalInventory,
    SimilarListingsFetcher
  } = nextStepsProps;

  // disable submit button after it is clicked once
  const [isButtonDisabled, disableButton] = useState(false);
  // success message, server error
  const [showAlert, toggleAlert] = useState(showEmailConfirmationModal);
  // This useEffect needs to exist since the changing of showEmailConfirmationModal does not always cause a render
  useEffect(() => {
    toggleAlert(showEmailConfirmationModal);
  }, [showEmailConfirmationModal]);
  const [showCTAForm, toggleCTAForm] = useState(ctaProps.show || false);
  const [serverError, setServerError] = useState('');
  const [submitState, setSubmitState] = useState('');
  const [inquirySelection, setInquirySelection] = useState(defaultInquiry);
  const [additionalInventoryInfo, setAdditionalInventoryInfo] = useState({});
  const [selectedListings, setSelectedListings] = useState([]);
  useEffect(() => {
    if (uiContext === 'testDrive') {
      setInquirySelection('scheduleTestDrive');
    }
  }, [uiContext]);

  // default to prop value, but provide a localized state if setter isn't passed in
  const [showEmailConfirmation, setShowEmailConfirmationFallback] = useState(showEmailConfirmationModal ?? false);
  const setShowEmailConfirmation = typeof setEmailConfirmationModal === 'function' ? setEmailConfirmationModal : setShowEmailConfirmationFallback;
  const additionalInventory = additionalInventoryInfo?.listings;
  const serverErrorRef = useRef(null);

  // for variants using the scheduler, handle local state of inquiry selection for formData
  const {
    inquiry: inquiryConfig
  } = EmailStaticContent;
  const handleOneClickEmailSendClick = useCallback(() => {
    const {
      errorMessage
    } = handleOneClickEmailSend();
    setOneClickEmailSent(!errorMessage);
  }, [handleOneClickEmailSend, setOneClickEmailSent]);
  const handleInquirySelection = useCallback(function (event, value, shouldFireTag) {
    if (value === void 0) {
      value = defaultInquiry;
    }
    if (shouldFireTag === void 0) {
      shouldFireTag = true;
    }
    const selection = event ? event?.target?.value : value;
    if (selection === 'scheduleTestDrive' && isAccelerateScheduleTestDrive) {
      setDealInfo();
    }
    setInquirySelection(selection);
    if (shouldFireTag) {
      sendClick('ownerServiceInquiry', event, {
        inquiryLabel: inquiryConfig[selection]?.label
      });
    }
  }, [inquiryConfig, isAccelerateScheduleTestDrive, setDealInfo]);
  const handleListingSelect = (event, newSelection) => {
    const selections = [...selectedListings];
    const index = selections.findIndex(selection => selection && selection.id === newSelection.id);
    if (index === -1) {
      selections.push(newSelection);
      sendClick('recentlyViewedVehicleNBAClick', event, {
        inventoryId: newSelection.id,
        hasSavedListings: additionalInventoryInfo?.hasSavedListings,
        isSimilarListingsEnabled
      });
    } else {
      selections.splice(index, 1);
    }
    setSelectedListings(selections);
    toggleAlert(false);
  };
  const handleSetOneClickChecked = useCallback(checked => {
    setOneClickChecked(checked);
  }, [setOneClickChecked]);
  useEffect(() => {
    if (hasDom() && enableDeals) {
      const {
        searchParams
      } = new URL(window.location.href);
      const inquirySelector = searchParams.get('inquiry');
      if (inquirySelector === 'scheduleTestDrive') {
        handleInquirySelection(null, 'scheduleTestDrive');
      }
    }
  }, [handleInquirySelection, enableDeals]);

  // Reset inquiry for vdp-vdp navigation
  useEffect(() => {
    setInquirySelection(defaultInquiry);
  }, [listingId]);
  useEffect(() => {
    setShowEmailConfirmationFallback(showEmailConfirmationModal);
  }, [showEmailConfirmationModal]);
  const showAdditionalInventory = useCallback(async () => {
    setAdditionalInventoryInfo((await getAdditionalInventory()) ?? {});
  }, [getAdditionalInventory, setAdditionalInventoryInfo]);
  useEffect(() => {
    if (oneClickAdditionalInventory && !additionalInventory?.length && typeof getAdditionalInventory === 'function' && uiContext === 'default') {
      showAdditionalInventory();
    } else {
      setAdditionalInventoryInfo({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oneClickAdditionalInventory]);
  const handleFormSubmit = useCallback(async formData => {
    const isVHRLeadGate = uiContext === 'vehicleHistoryReport';

    // Disable submit button to avoid duplicated leads
    disableButton(true);
    setSubmitState('loading');

    // Test drives are sent in the user's timezone, but operating hours are in the dealer's timezone
    // We are okay with this inconsistency because scheduling is finalized between dealer/client
    if (inquiryConfig[inquirySelection]?.schedule) {
      formData.timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;
      formData.comments = `I'd like to test drive your ${listing?.listingTitle} on ${formData?.date} at ${formData?.time} ${new Date().toLocaleTimeString('en-us', {
        timeZoneName: 'short'
      }).split(' ')[2]}`;
    }
    if (uiContext === 'default') {
      formData.serviceInquiry = inquirySelection;
      formData.isOneClickOptedIn = oneClickChecked;
    }

    // report status of one click check box & formData when saving changes
    if (uiContext === 'edit') {
      formData.isOneClickOptedIn = oneClickChecked;
      handleSaveChanges(oneClickChecked, formData);
    }
    if (uiContext === 'multi') {
      handleAdditionalInventoryModalClose();
    }
    if (uiContext === 'cta') {
      handleAdditionalInventoryModalOpen();
    }
    if (isVHRLeadGate) {
      formData.comments = 'Vehicle History Report requested.';
    }
    if (ndsProps.walletOptIn && (uiContext !== 'edit' || !formData.isOneClickOptedIn) && !isVHRLeadGate) {
      formData.includeWalletData = true;
    }
    if (!isEditEmailModal) {
      serverErrorRef.current = await onEmailSubmit(formData, inquirySelection, selectedListings, additionalInventoryInfo?.hasSavedListings);
    }
    if (!serverErrorRef.current && !isIncentivesSheet) {
      let additionalInventoryInfoTemp = {};

      // if no server error, reset the server error state
      setServerError(null);

      // when uiContext is additionalInventory, additionalInventory is set on mount
      if (!additionalInventory?.length && typeof getAdditionalInventory === 'function' && !isEditEmailModal) {
        additionalInventoryInfoTemp = (await getAdditionalInventory()) ?? {};
        const additionalInventoryLength = additionalInventoryInfoTemp?.listings?.length ?? 0;
        if (typeof SimilarListingsFetcher === 'function' && isSimilarListingsEnabled && zip && additionalInventoryLength < 3) {
          let minPrice;
          let maxPrice;
          if (pricingDetail && (pricingDetail?.salePrice || pricingDetail?.incentive)) {
            const salePrice = pricingDetail?.incentive || pricingDetail?.salePrice;
            if (salePrice >= 30000) {
              minPrice = Math.round(salePrice * 0.95);
              maxPrice = Math.round(salePrice * 1.05);
            } else {
              minPrice = Math.round(salePrice * 0.90);
              maxPrice = Math.round(salePrice * 1.10);
            }
          }
          const searchQuery = {
            bodyStyleCode: listing?.bodyStyleCodes?.[0] ?? listing?.bodyStyles?.[0]?.code,
            channel: channel === 'ATC,PREVIEW' ? 'ATC' : channel,
            classSeriesCode: listing?.classSeriesCode,
            dealerId: null,
            driveCode: listing?.driveGroup ?? '',
            excludedListingId: listingId,
            extColorSimple: listing?.exteriorColorSimple ?? '',
            listingType: listing?.type?.toUpperCase() ?? '',
            makeCode: listing?.makeCode ?? listing?.make?.code,
            modelCode: listing.modelCode ?? listing?.model?.code,
            minPrice,
            maxPrice,
            numRecords: 20,
            ownerProduct: 'INVENTORY_CONQUEST',
            searchRadius: 100,
            startYear: listing?.year ? listing.year - 1 : '',
            endYear: listing?.year ? listing.year + 1 : '',
            transmissionCode: listing?.transmission?.name ?? '',
            zip
          };
          const similarListingsResponse = await SimilarListingsFetcher(searchQuery);
          const similarListings = similarListingsResponse?.listings;
          const filteredSimilarListings = similarListings ? similarListings?.slice(0, 3 - additionalInventoryLength) : [];
          if (!additionalInventoryLength) {
            additionalInventoryInfoTemp = {
              listings: filteredSimilarListings,
              hasSavedListings: false
            };
          } else if (additionalInventoryLength < 3) {
            additionalInventoryInfoTemp?.listings?.push(...filteredSimilarListings);
          }
        }
        setAdditionalInventoryInfo(additionalInventoryInfoTemp);
      } else {
        setAdditionalInventoryInfo({});
      }
      // opens the email confirmation modal
      setShowEmailConfirmation(true);
      if (additionalInventoryInfoTemp?.listings?.length) {
        sendImpressions({
          name: 'nbaRecentlyViewedImpression',
          data: {
            inventoryId: listingId,
            hasSavedListings: additionalInventoryInfoTemp?.hasSavedListings,
            isSimilarListingsEnabled
          }
        });
      }
      // CTA - hide CTA SideBarToModal when email is sent successfully
      if (showCTAForm && !additionalInventory?.length) {
        toggleCTAForm(false);
      }
      if (useFallbackLead && window && window.newrelic) {
        window.newrelic.addPageAction('ThirdPartyAction', {
          id: listingId,
          vin: listing.vin,
          component: 'EmailOwner',
          message: 'User submitted fallback lead after NDS Calculator Third Party Lead failure'
        });
      }
      if (isVHRLeadGate) {
        handleVHROpenNewTab();
      }
    } else if (serverErrorRef?.current) {
      setServerError(serverErrorRef.current);
    } else if (isIncentivesSheet) {
      handleIncentivesSheetClose();
    }
    // show success message or server error
    toggleAlert(true);
    // show oneClick success state if no server error
    if (uiContext !== 'multi' && uiContext !== 'edit') {
      setOneClickEmailSent(true);
    }
    // Enable button again so user can try sending another email
    disableButton(false);
    setSubmitState('');
  }, [handleAdditionalInventoryModalOpen, uiContext, inquiryConfig, inquirySelection, ndsProps.walletOptIn, isEditEmailModal, listing?.listingTitle, listing?.bodyStyleCodes, listing?.bodyStyles, listing?.classSeriesCode, listing?.driveGroup, listing?.exteriorColorSimple, listing?.type, listing?.makeCode, listing?.make?.code, listing.modelCode, listing?.model?.code, listing.year, listing?.transmission?.name, listing.vin, oneClickChecked, handleSaveChanges, handleAdditionalInventoryModalClose, onEmailSubmit, selectedListings, additionalInventoryInfo?.hasSavedListings, additionalInventory?.length, getAdditionalInventory, setShowEmailConfirmation, showCTAForm, useFallbackLead, SimilarListingsFetcher, isSimilarListingsEnabled, zip, pricingDetail, channel, listingId, handleVHROpenNewTab, setOneClickEmailSent, handleIncentivesSheetClose, isIncentivesSheet]);
  const sharedProps = {
    emailCTAContext,
    buttonStyle,
    setShowEmailConfirmation,
    formattedFilterValues,
    handleFormSubmit,
    handleSaveChanges,
    isButtonDisabled,
    isEditEmailModal,
    inquiryProps: {
      handleInquirySelection,
      inquirySelection
    },
    isIncentivesSheet,
    isSm,
    isXs,
    ndsProps,
    phoneClickHandler,
    oneClickChecked,
    setOneClickChecked: handleSetOneClickChecked,
    setOneClickEmailSent,
    showAlert,
    userProfile,
    serverError,
    submitState,
    userDuck,
    dealsDuck,
    isAccelerateScheduleTestDrive,
    hideTestDrive,
    nextSteps,
    handleEmailInputInteraction,
    handleEditSettingsClick,
    messageOptIn,
    setMessageOptIn,
    isLoggedIn
  };
  let emailOwnerType;
  if (showEmailConfirmation && !!additionalInventory && additionalInventory?.length) {
    uiContext = 'multi';
  }
  const defaultEmailForm = /*#__PURE__*/_jsx(EmailForm, {
    ...sharedProps,
    alternateFormContent: alternateFormContent,
    isConfirmation: false,
    handleOneClickEmailSendClick: handleOneClickEmailSendClick,
    showOneClickSuccess: oneClickEmailSent
  });
  switch (uiContext) {
    case 'cta':
    case 'calculator':
    case 'calculatorOfferSummary':
    case 'calculatorBreakdownConfirmation':
    case 'edit':
    case 'quote':
    case 'split':
    case 'testDrive':
    case 'vehicleHistoryReport':
      ctaProps.show = showCTAForm;
      emailOwnerType = /*#__PURE__*/_jsx(EmailSideBarToModal, {
        ...sharedProps,
        ctaProps: ctaProps,
        setServerError: setServerError,
        showConfirmation: showEmailConfirmation,
        title: sideBarToModalTitle,
        toggleAlert: toggleAlert,
        additionalContent: additionalContent,
        footerContent: footerContent
      });
      break;
    default:
      emailOwnerType = defaultEmailForm;
  }
  delete rest.dispatch;
  delete rest.ownerId;
  delete rest.activeEmailListingDuck;
  const providerValue = useMemo(() => ({
    uiContext,
    listing,
    owner
  }), [uiContext, listing, owner]);
  return /*#__PURE__*/_jsx(EmailContext.Provider, {
    value: providerValue,
    children: /*#__PURE__*/_jsxs("div", {
      "data-cmp": "emailOwner",
      ...rest,
      children: [emailOwnerType, showEmailConfirmation && !uiContext?.includes('wallet') && /*#__PURE__*/_jsx(EmailSideBarToModal, {
        ...sharedProps,
        ctaProps: ctaProps,
        isConfirmation: true,
        setServerError: setServerError,
        showConfirmation: showEmailConfirmation,
        toggleAlert: toggleAlert,
        additionalInventoryInfo: additionalInventoryInfo,
        setAdditionalInventoryInfo: setAdditionalInventoryInfo,
        handleListingSelect: handleListingSelect,
        selectedListings: selectedListings,
        setSelectedListings: setSelectedListings
      })]
    })
  });
});
export default EmailOwner;